import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { SWITCH_SLIDE_MS } from "../../lib/helpers"

// A frame visit rebuilds this element, so the id to refocus and the field being typed in
// (value and caret) outlive the instance.
let pendingFocus = null
let liveField = null
let pendingTop = null

// Every change builds on the pending URL (the frame's src, which Turbo sets at once), not on the
// controls on screen, which still show the previous response while a request runs.
export default class extends Controller {
  static targets = ["queue", "queueInput", "switch", "field", "error"]

  connect() {
    this.frameId = this.element.closest("turbo-frame").id
    this.#keepTyping = this.#keepTyping.bind(this)
    this.element.addEventListener("turbo:before-morph-element", this.#keepTyping)
    if (pendingFocus && Date.now() - pendingFocus.at < 10000) document.getElementById(pendingFocus.id)?.focus()
    pendingFocus = null
    if (pendingTop && Date.now() - pendingTop.at < 10000) this.#keepPlace(pendingTop.top)
    pendingTop = null
    if (liveField) this.#resumeTyping()
  }

  // The frame swap drops the scroll anchor, so the browser pins a later element and the card
  // jumps by however much the code below it grew or shrank. Put the card back where it was.
  #keepPlace(top) {
    const shift = this.element.getBoundingClientRect().top - top
    if (shift) this.#scroller().scrollBy(0, shift)
  }

  #scroller() {
    for (let element = this.element.parentElement; element; element = element.parentElement) {
      if (/auto|scroll/.test(getComputedStyle(element).overflowY) && element.scrollHeight > element.clientHeight) {
        return element
      }
    }
    return document.scrollingElement
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-morph-element", this.#keepTyping)
    clearTimeout(this.typeTimer)
    // A response to an earlier change replaced the switch mid-slide: send the flip now.
    if (this.pendingToggle) {
      const url = this.pendingToggle
      pendingFocus = { id: this.toggleFocus, at: Date.now() }
      this.#releaseToggle()
      if (document.getElementById(this.frameId)) this.#visit(url)
    }
  }

  // A periodic refresh morphs the Setup page frame in place: keep what is typed but not sent.
  #keepTyping(event) {
    const element = event.target
    const typing = element instanceof HTMLInputElement &&
      (element === document.activeElement || element.value !== (element.getAttribute("value") ?? ""))
    if (typing || (this.errorTargets.includes(element) && !element.hidden)) event.preventDefault()
  }

  // Value fields update the code as you type, once the value is valid (blank is the default).
  type({ target }) {
    liveField = { id: target.id, value: target.value, caret: target.selectionStart }
    clearTimeout(this.typeTimer)
    const valid = this.#valid(target)
    if (valid) target.removeAttribute("aria-invalid")
    else target.setAttribute("aria-invalid", "true")
    const error = this.errorTargets.find((element) => element.id === `${target.id}-error`)
    if (error) error.hidden = valid
    if (valid) this.typeTimer = setTimeout(() => this.#sendField(target, target.id), 400)
  }

  // Keystrokes that landed while the request ran are not in the new field yet: put them back.
  #resumeTyping() {
    const { id, value, caret } = liveField
    liveField = null
    const field = document.getElementById(id)
    if (!field || !this.element.contains(field) || document.activeElement !== field) return

    if (field.value !== value) {
      field.value = value
      if (this.fieldTargets.includes(field)) this.type({ target: field })
    }
    field.setSelectionRange(caret, caret)
  }

  #valid(field) {
    const value = field.value.trim()
    if (value === "") return true

    return value.length <= field.maxLength && new RegExp(`^(?:${field.dataset.pattern})$`).test(value)
  }

  // Enter sends at once. A change (leaving the field) waits a tick so focus lands where it went.
  commit(event) {
    const field = event.target
    if (event.type === "keydown") event.preventDefault()
    clearTimeout(this.typeTimer)
    if (!this.#valid(field)) return

    if (event.type === "keydown") this.#sendField(field, field.id)
    else setTimeout(() => this.#sendField(field, document.activeElement?.id), 0)
  }

  #sendField(field, focus) {
    const options = this.#options()
    const value = field.value.trim()
    if (value) options[field.dataset.key] = value
    else delete options[field.dataset.key]
    this.#navigate({ options }, focus)
  }

  queueTyped({ target }) {
    liveField = { id: target.id, value: target.value, caret: target.selectionStart }
  }

  queueKeydown(event) {
    const input = event.currentTarget
    const pending = input.value.trim() !== ""

    if (event.key === "," || ((event.key === "Enter" || event.key === "Tab") && pending)) {
      event.preventDefault()
      this.#addQueue(input)
    } else if (event.key === "Enter") {
      event.preventDefault()
    } else if (event.key === "Backspace" && !pending) {
      const queues = this.#queues()
      if (queues.length === 0) return
      event.preventDefault()
      this.#navigate({ queues: queues.slice(0, -1) }, input.id)
    }
  }

  // The frame swap also blurs the replaced input, which is gone by the next tick.
  queueBlur({ target }) {
    setTimeout(() => {
      if (target.isConnected && target.value.trim()) this.#addQueue(target, document.activeElement?.id)
    }, 0)
  }

  #addQueue(input, focus = input.id) {
    const names = input.value.split(",").map((name) => name.trim()).filter(Boolean)
    input.value = ""
    if (!liveField || liveField.id === input.id) liveField = { id: input.id, value: "", caret: 0 }
    if (names.length === 0) return

    const queues = this.#queues()
    for (const name of names) if (!queues.includes(name)) queues.push(name)
    this.#navigate({ queues }, focus)
  }

  removeQueue({ currentTarget }) {
    const name = currentTarget.closest("[data-queue]").dataset.queue
    this.#navigate({ queues: this.#queues().filter((queue) => queue !== name) }, this.queueInputTarget.id)
  }

  // The frame response replaces the switch, so the knob finishes its slide first.
  // Holding periodic refreshes meanwhile keeps a morph from reverting the flip.
  toggle({ currentTarget }) {
    const on = currentTarget.getAttribute("aria-checked") !== "true"
    currentTarget.setAttribute("aria-checked", String(on))
    const options = this.#options()
    if (on) options[currentTarget.dataset.key] = "1"
    else delete options[currentTarget.dataset.key]

    if (this.pendingToggle) clearTimeout(this.toggleTimer)
    else document.dispatchEvent(new CustomEvent("fsp:increment"))
    this.pendingToggle = this.#url({ queues: this.#queues(), options })
    this.toggleFocus = currentTarget.id
    this.toggleTimer = setTimeout(() => this.#navigate({}, currentTarget.id), SWITCH_SLIDE_MS)
  }

  #releaseToggle() {
    clearTimeout(this.toggleTimer)
    this.pendingToggle = null
    document.dispatchEvent(new CustomEvent("fsp:decrement"))
  }

  reset() {
    if (this.pendingToggle) this.#releaseToggle()
    pendingFocus = { id: this.queueInputTarget.id, at: Date.now() }
    this.#visit(this.#url(null))
  }

  #queues() {
    const param = new URL(this.#current(), window.location.origin).searchParams.get("queues")
    if (param !== null) return param.split(",").filter(Boolean)
    return this.queueTargets.map((element) => element.dataset.queue)
  }

  #options() {
    const values = {}
    for (const [key, value] of new URL(this.#current(), window.location.origin).searchParams) {
      const match = key.match(/^macro\[(.+)\]$/)
      if (match) values[match[1]] = value
    }
    return values
  }

  // A pending switch flip is part of the state, so a change made during its slide carries it.
  #navigate({ queues = this.#queues(), options = this.#options() } = {}, focus) {
    const url = this.#url({ queues, options })
    if (this.pendingToggle) this.#releaseToggle()
    if (url === this.#current()) return

    pendingFocus = focus ? { id: focus, at: Date.now() } : null
    this.#visit(url)
  }

  // A frame keeps its URL in src once it navigates. Before that the new-manager frame names it in
  // data-src, and the Setup page frame is the page URL.
  #current() {
    if (this.pendingToggle) return this.pendingToggle
    const frame = document.getElementById(this.frameId)
    const url = new URL(frame?.src || frame?.dataset.src || window.location.href, window.location.origin)
    return url.pathname + url.search
  }

  #url(config) {
    const url = new URL(this.#current(), window.location.origin)
    for (const key of [...url.searchParams.keys()]) {
      if (key === "queues" || key.startsWith("macro[")) url.searchParams.delete(key)
    }
    if (config) {
      url.searchParams.set("queues", config.queues.join(","))
      for (const [key, value] of Object.entries(config.options)) url.searchParams.set(`macro[${key}]`, value)
    }
    return url.pathname + url.search
  }

  // The Setup page frame carries data-turbo-action="replace", so its URL follows the choices.
  #visit(url) {
    if (this.element.isConnected) pendingTop = { top: this.element.getBoundingClientRect().top, at: Date.now() }
    Turbo.visit(url, { frame: this.frameId })
  }
}
